import { useEffect, useRef, useState } from 'react';
import { haversineDistanceM, OpenStreetMapProvider } from '@freedom/geospatial';
import type { ClientAvailability, Coordinates, DesignSite, LinkQuality, ManualObstacle, ProjectViewState, SiteType } from '@freedom/shared';
import type { ObstacleFeature } from '@freedom/elevation';
import type { CoverageOverlay } from './coverage-overlay';
import type { DisplaySettings, MapStyleId, RadioLinkKind, RadioLinkModel } from './radio-workspace';
import { profileMapRouteCoordinates } from './profile-map-coordinate';
import {
  beginTwoPointMapDraft,
  completeTwoPointMapDraft,
  emptyTwoPointMapDraft,
  mapToolCursor,
  previewTwoPointMapDraft,
  type MapToolId,
  type TwoPointMapDraft,
} from './map-tool-state';

export type { MapToolId } from './map-tool-state';

export interface MapContextPoint extends Coordinates { x: number; y: number }
export interface MapScreenPoint { x: number; y: number }
export interface MapCommand { id: number; type: 'zoom-in' | 'zoom-out' | 'locate' }
export interface MapRadioEndpoint { siteId: string | null; coordinates: Coordinates }

interface MapLibreControl {}
interface MapLibreSource { setData(data: GeoJsonCollection): void; updateImage?(options: { url: string; coordinates: number[][] }): void }
interface MapLibreMarker { setLngLat(coordinates: [number, number]): MapLibreMarker; addTo(map: MapLibreMap): MapLibreMarker; remove(): void }
interface MapPoint { x: number; y: number }
interface MapMouseEvent { lngLat: { lng: number; lat: number }; point: MapPoint; preventDefault(): void }
interface MapCanvas { style: { cursor: string } }
interface MapFeature { properties?: Record<string, string | number> }
interface DragPanControl { disable(): void; enable(): void }
interface MapLibreMap {
  addControl(control: MapLibreControl, position: string): void;
  addSource(id: string, source: Record<string, unknown>): void;
  addLayer(layer: Record<string, unknown>): void;
  getSource(id: string): MapLibreSource | undefined;
  getCanvas(): MapCanvas;
  getCenter(): { lng: number; lat: number };
  getZoom(): number;
  getPitch(): number;
  getBearing(): number;
  unproject(point: MapPoint): { lng: number; lat: number };
  project(coordinates: [number, number]): MapPoint;
  panBy(offset: [number, number], options?: Record<string, unknown>): void;
  setMaxZoom(maxZoom: number): void;
  setStyle(style: Record<string, unknown>, options?: { diff?: boolean }): void;
  setPaintProperty(layerId: string, property: string, value: unknown): void;
  setLayoutProperty(layerId: string, property: string, value: unknown): void;
  setFilter(layerId: string, filter: unknown[]): void;
  flyTo(options: Record<string, unknown>): void;
  jumpTo(options: Record<string, unknown>): void;
  queryRenderedFeatures(point: MapPoint, options: Record<string, unknown>): MapFeature[];
  dragPan: DragPanControl;
  on(event: string, handler: (event: MapMouseEvent) => void): void;
  remove(): void;
}
interface MapLibreConstructor { new (options: Record<string, unknown>): MapLibreMap }
interface MapLibreApi {
  Map: MapLibreConstructor;
  Marker: new (options: { element: HTMLElement; anchor: 'center' }) => MapLibreMarker;
  NavigationControl: new (options: Record<string, unknown>) => MapLibreControl;
  AttributionControl: new (options: Record<string, unknown>) => MapLibreControl;
}
interface GeoJsonGeometry { type: 'Point' | 'LineString' | 'Polygon'; coordinates: number[] | number[][] | number[][][] }
interface GeoJsonFeature { type: 'Feature'; properties: Record<string, string | number>; geometry: GeoJsonGeometry }
interface GeoJsonCollection { type: 'FeatureCollection'; features: GeoJsonFeature[] }

declare global {
  interface Window { maplibregl?: MapLibreApi }
}

export function siteCollection(sites: DesignSite[], selectedSiteId: string | null, clientStatuses: Record<string, ClientAvailability>, links: RadioLinkModel[] = []): GeoJsonCollection {
  const linkedSiteIds = new Set(links.flatMap((link) => [link.startSiteId, link.endSiteId]));
  return {
    type: 'FeatureCollection',
    features: sites.map((site) => ({
      type: 'Feature',
      properties: { id: site.id, label: site.name, type: site.type, selected: site.id === selectedSiteId ? 1 : 0, linked: linkedSiteIds.has(site.id) ? 1 : 0, availability: clientStatuses[site.id] ?? 'unknown' },
      geometry: { type: 'Point', coordinates: [site.coordinates.lon, site.coordinates.lat] },
    })),
  };
}

export function linkCollection(sites: DesignSite[], links: RadioLinkModel[], selectedLinkId: string | null, quality: LinkQuality | null): GeoJsonCollection {
  return {
    type: 'FeatureCollection',
    features: links.flatMap((link): GeoJsonFeature[] => {
      const start = sites.find((site) => site.id === link.startSiteId);
      const end = sites.find((site) => site.id === link.endSiteId);
      if (!start || !end) return [];
      return [{
        type: 'Feature',
        properties: { id: link.id, kind: link.kind, selected: link.id === selectedLinkId ? 1 : 0, quality: link.id === selectedLinkId ? quality ?? 'pending' : 'saved' },
        geometry: { type: 'LineString', coordinates: profileMapRouteCoordinates(start.coordinates, end.coordinates).map((point) => [point.lon, point.lat]) },
      }];
    }),
  };
}

export function linkEndpointCollection(sites: DesignSite[], links: RadioLinkModel[], selectedLinkId: string | null, quality: LinkQuality | null): GeoJsonCollection {
  const endpoints = new Map<string, GeoJsonFeature>();
  for (const link of links) {
    const start = sites.find((site) => site.id === link.startSiteId);
    const end = sites.find((site) => site.id === link.endSiteId);
    if (!start || !end) continue;
    const selected = link.id === selectedLinkId ? 1 : 0;
    const linkQuality = link.id === selectedLinkId ? quality ?? 'pending' : 'saved';
    for (const [role, site] of [['start', start], ['end', end]] as const) {
      const existing = endpoints.get(site.id);
      if (existing && Number(existing.properties.selected) >= selected) continue;
      endpoints.set(site.id, {
        type: 'Feature',
        properties: { id: site.id, linkId: link.id, kind: link.kind, role, selected, quality: linkQuality },
        geometry: { type: 'Point', coordinates: [site.coordinates.lon, site.coordinates.lat] },
      });
    }
  }
  return { type: 'FeatureCollection', features: [...endpoints.values()] };
}

function syncLinkEndpointMarkers(map: MapLibreMap, maplibre: MapLibreApi, registry: Map<string, { marker: MapLibreMarker; element: HTMLElement }>, sites: DesignSite[], links: RadioLinkModel[], selectedLinkId: string | null, quality: LinkQuality | null): void {
  const activeIds = new Set<string>();
  for (const feature of linkEndpointCollection(sites, links, selectedLinkId, quality).features) {
    const id = String(feature.properties.id);
    const coordinates = feature.geometry.coordinates as number[];
    if (coordinates.length < 2 || !Number.isFinite(coordinates[0]) || !Number.isFinite(coordinates[1])) continue;
    activeIds.add(id);
    let entry = registry.get(id);
    if (!entry) {
      const element = document.createElement('span');
      element.setAttribute('aria-hidden', 'true');
      const marker = new maplibre.Marker({ element, anchor: 'center' }).setLngLat([coordinates[0]!, coordinates[1]!]).addTo(map);
      entry = { marker, element };
      registry.set(id, entry);
    } else entry.marker.setLngLat([coordinates[0]!, coordinates[1]!]);
    entry.element.classList.add('radio-link-endpoint-marker');
    entry.element.classList.toggle('selected', Number(feature.properties.selected) === 1);
    entry.element.classList.toggle('quality-pending', feature.properties.quality === 'pending');
    entry.element.classList.toggle('quality-saved', feature.properties.quality === 'saved');
    entry.element.classList.toggle('quality-marginal', feature.properties.quality === 'marginal');
    entry.element.classList.toggle('quality-blocked', feature.properties.quality === 'blocked');
    entry.element.classList.toggle('ptmp', feature.properties.kind === 'ptmp');
  }
  for (const [id, entry] of registry) {
    if (activeIds.has(id)) continue;
    entry.marker.remove();
    registry.delete(id);
  }
}

function profileHoverCollection(coordinates: Coordinates | null): GeoJsonCollection {
  return {
    type: 'FeatureCollection',
    features: coordinates ? [{ type: 'Feature', properties: { role: 'profile-hover' }, geometry: { type: 'Point', coordinates: [coordinates.lon, coordinates.lat] } }] : [],
  };
}

function ptpDraftCollection(start: MapRadioEndpoint | null, end: Coordinates | null): GeoJsonCollection {
  if (!start) return { type: 'FeatureCollection', features: [] };
  const destination = end ?? start.coordinates;
  return {
    type: 'FeatureCollection',
    features: [
      { type: 'Feature', properties: { role: 'preview' }, geometry: { type: 'LineString', coordinates: [[start.coordinates.lon, start.coordinates.lat], [destination.lon, destination.lat]] } },
      { type: 'Feature', properties: { role: 'start' }, geometry: { type: 'Point', coordinates: [start.coordinates.lon, start.coordinates.lat] } },
      { type: 'Feature', properties: { role: 'end' }, geometry: { type: 'Point', coordinates: [destination.lon, destination.lat] } },
    ],
  };
}

function measurementLabel(distanceM: number): string {
  return distanceM >= 1_000 ? `${(distanceM / 1_000).toFixed(2)} км` : `${Math.round(distanceM)} м`;
}

function measureDraftCollection(draft: TwoPointMapDraft): GeoJsonCollection {
  if (!draft.start) return { type: 'FeatureCollection', features: [] };
  const destination = draft.preview ?? draft.start;
  const distanceM = haversineDistanceM(draft.start, destination);
  return {
    type: 'FeatureCollection',
    features: [
      { type: 'Feature', properties: { role: 'measurement', label: measurementLabel(distanceM) }, geometry: { type: 'LineString', coordinates: [[draft.start.lon, draft.start.lat], [destination.lon, destination.lat]] } },
      { type: 'Feature', properties: { role: 'start' }, geometry: { type: 'Point', coordinates: [draft.start.lon, draft.start.lat] } },
      { type: 'Feature', properties: { role: 'end' }, geometry: { type: 'Point', coordinates: [destination.lon, destination.lat] } },
    ],
  };
}

function obstacleCollection(obstacles: ManualObstacle[]): GeoJsonCollection {
  return { type: 'FeatureCollection', features: obstacles.map((obstacle) => ({ type: 'Feature', properties: { id: obstacle.id, label: obstacle.name, heightM: obstacle.heightM }, geometry: { type: 'Point', coordinates: [obstacle.coordinates.lon, obstacle.coordinates.lat] } })) };
}

function detectedObstacleCollection(obstacles: ObstacleFeature[]): GeoJsonCollection {
  return {
    type: 'FeatureCollection',
    features: obstacles.map((obstacle) => {
      const pairs = obstacle.geometry.map((point) => [point.lon, point.lat]);
      const isPolygon = pairs.length >= 3;
      const polygonPairs = isPolygon && pairs[0] && pairs[pairs.length - 1] && (pairs[0][0] !== pairs[pairs.length - 1]?.[0] || pairs[0][1] !== pairs[pairs.length - 1]?.[1]) ? [...pairs, pairs[0]] : pairs;
      return { type: 'Feature', properties: { id: obstacle.id, kind: obstacle.kind, heightM: obstacle.heightM, estimated: obstacle.estimated ? 1 : 0 }, geometry: isPolygon ? { type: 'Polygon', coordinates: [polygonPairs] } : { type: 'Point', coordinates: pairs[0] ?? [0, 0] } };
    }),
  };
}

function coverageBoundaryCollection(boundary: Coordinates[], showWeak = true): GeoJsonCollection {
  if (boundary.length < 4) return { type: 'FeatureCollection', features: [] };
  const origin = boundary[0] as Coordinates;
  const scales = showWeak ? [1, 0.78, 0.56, 0.34] : [0.78, 0.56, 0.34];
  return { type: 'FeatureCollection', features: scales.map((scale, index) => ({ type: 'Feature', properties: { strength: showWeak ? index : index + 1 }, geometry: { type: 'Polygon', coordinates: [boundary.map((point) => [origin.lon + (point.lon - origin.lon) * scale, origin.lat + (point.lat - origin.lat) * scale])] } })) };
}

function imageCoordinates(overlay: CoverageOverlay): number[][] {
  return [[overlay.bounds.west, overlay.bounds.north], [overlay.bounds.east, overlay.bounds.north], [overlay.bounds.east, overlay.bounds.south], [overlay.bounds.west, overlay.bounds.south]];
}

const TRANSPARENT_PIXEL = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=';

const toolSiteTypes: Partial<Record<MapToolId, SiteType>> = {
  site: 'site',
  base_station: 'base_station',
  client: 'client',
};

const ESRI_IMAGERY_ATTRIBUTION = 'Esri, Vantor, Earthstar Geographics, and the GIS User Community';

export function mapMaximumZoom(style: MapStyleId): number {
  return style === 'terrain' ? 17 : 19;
}

export function buildMapStyle(style: MapStyleId): Record<string, unknown> {
  if (style === 'hybrid') {
    const osmStyle = new OpenStreetMapProvider().createStyle();
    return {
      version: 8,
      glyphs: osmStyle.glyphs,
      sources: {
        satellite: { type: 'raster', tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'], tileSize: 256, maxzoom: 19, attribution: ESRI_IMAGERY_ATTRIBUTION },
        reference: { ...(osmStyle.sources.osm as Record<string, unknown>), maxzoom: 19 },
      },
      layers: [
        { id: 'satellite', type: 'raster', source: 'satellite' },
        {
          id: 'hybrid-reference',
          type: 'raster',
          source: 'reference',
          paint: {
            'raster-opacity': ['interpolate', ['linear'], ['zoom'], 5, 0.16, 12, 0.28, 17, 0.42],
            'raster-saturation': -0.55,
            'raster-contrast': 0.25,
            'raster-fade-duration': 0,
          },
        },
      ],
    };
  }
  if (style === 'satellite') {
    return { version: 8, glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf', sources: { basemap: { type: 'raster', tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'], tileSize: 256, maxzoom: 19, attribution: ESRI_IMAGERY_ATTRIBUTION } }, layers: [{ id: 'basemap', type: 'raster', source: 'basemap' }] };
  }
  if (style === 'terrain') {
    return { version: 8, glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf', sources: { basemap: { type: 'raster', tiles: ['https://tile.opentopomap.org/{z}/{x}/{y}.png'], tileSize: 256, maxzoom: 17, attribution: '© OpenTopoMap contributors' } }, layers: [{ id: 'basemap', type: 'raster', source: 'basemap' }] };
  }
  const osmStyle = new OpenStreetMapProvider().createStyle();
  return { ...osmStyle, sources: { ...osmStyle.sources, osm: { ...(osmStyle.sources.osm as Record<string, unknown>), maxzoom: 19 } } } as unknown as Record<string, unknown>;
}

export function MapWorkspace({ sites, obstacles, detectedObstacles, coverageOverlay, interferenceOverlay, coverageBoundary, clientStatuses, radioLinks, selectedRadioLinkId, linkQuality, profileHoverCoordinates, selectedSiteId, activeTool, radioDraftStartSiteId, focusCoordinates, viewState, viewResetKey, displaySettings, command, onViewStateChange, onSelectSite, onSelectRadioLink, onSelectedSiteAnchorChange, onCreateRadio, onCreateSite, onCreateObstacle, onMoveSite, onMoveSiteStart, onMoveSiteEnd, onContextMenu, onCancelActiveTool, onRadioDraftStageChange }: {
  sites: DesignSite[];
  obstacles: ManualObstacle[];
  detectedObstacles: ObstacleFeature[];
  coverageOverlay: CoverageOverlay | null;
  interferenceOverlay: CoverageOverlay | null;
  coverageBoundary: Coordinates[];
  clientStatuses: Record<string, ClientAvailability>;
  radioLinks: RadioLinkModel[];
  selectedRadioLinkId: string | null;
  linkQuality: LinkQuality | null;
  profileHoverCoordinates: Coordinates | null;
  selectedSiteId: string | null;
  activeTool: MapToolId;
  radioDraftStartSiteId: string | null;
  focusCoordinates: Coordinates | null;
  viewState: ProjectViewState;
  viewResetKey: string | null;
  displaySettings: DisplaySettings;
  command: MapCommand | null;
  onViewStateChange(viewState: Omit<ProjectViewState, 'activeLayers' | 'selectedObjectId'>): void;
  onSelectSite(siteId: string | null): void;
  onSelectRadioLink(linkId: string): void;
  onSelectedSiteAnchorChange(point: MapScreenPoint | null): void;
  onCreateRadio(kind: RadioLinkKind, endpoints: MapRadioEndpoint[]): void;
  onCreateSite(coordinates: Coordinates, type: SiteType): void;
  onCreateObstacle(coordinates: Coordinates): void;
  onMoveSite(siteId: string, coordinates: Coordinates): void;
  onMoveSiteStart(): void;
  onMoveSiteEnd(): void;
  onContextMenu(point: MapContextPoint): void;
  onCancelActiveTool(): void;
  onRadioDraftStageChange(active: boolean): void;
}): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const sitesRef = useRef(sites);
  const activeToolRef = useRef(activeTool);
  const selectedSiteIdRef = useRef(selectedSiteId);
  const callbacksRef = useRef({ onSelectSite, onSelectRadioLink, onSelectedSiteAnchorChange, onCreateRadio, onCreateSite, onCreateObstacle, onMoveSite, onMoveSiteStart, onMoveSiteEnd, onContextMenu, onCancelActiveTool, onRadioDraftStageChange });
  const radioLinksRef = useRef(radioLinks);
  const selectedRadioLinkIdRef = useRef(selectedRadioLinkId);
  const linkQualityRef = useRef(linkQuality);
  const profileHoverCoordinatesRef = useRef(profileHoverCoordinates);
  const obstaclesRef = useRef(obstacles);
  const detectedObstaclesRef = useRef(detectedObstacles);
  const coverageOverlayRef = useRef(coverageOverlay);
  const interferenceOverlayRef = useRef(interferenceOverlay);
  const coverageBoundaryRef = useRef(coverageBoundary);
  const clientStatusesRef = useRef(clientStatuses);
  const displaySettingsRef = useRef(displaySettings);
  const draggingSiteIdRef = useRef<string | null>(null);
  const linkEndpointMarkersRef = useRef(new Map<string, { marker: MapLibreMarker; element: HTMLElement }>());
  const ptpDraftStartRef = useRef<MapRadioEndpoint | null>(null);
  const measureDraftRef = useRef<TwoPointMapDraft>(emptyTwoPointMapDraft());
  const viewStateRef = useRef(viewState);
  const onViewStateChangeRef = useRef(onViewStateChange);
  const [loadFailed, setLoadFailed] = useState(false);
  const [waitingForLibrary, setWaitingForLibrary] = useState(false);

  sitesRef.current = sites;
  activeToolRef.current = activeTool;
  selectedSiteIdRef.current = selectedSiteId;
  callbacksRef.current = { onSelectSite, onSelectRadioLink, onSelectedSiteAnchorChange, onCreateRadio, onCreateSite, onCreateObstacle, onMoveSite, onMoveSiteStart, onMoveSiteEnd, onContextMenu, onCancelActiveTool, onRadioDraftStageChange };
  radioLinksRef.current = radioLinks;
  selectedRadioLinkIdRef.current = selectedRadioLinkId;
  linkQualityRef.current = linkQuality;
  profileHoverCoordinatesRef.current = profileHoverCoordinates;
  obstaclesRef.current = obstacles;
  detectedObstaclesRef.current = detectedObstacles;
  coverageOverlayRef.current = coverageOverlay;
  interferenceOverlayRef.current = interferenceOverlay;
  coverageBoundaryRef.current = coverageBoundary;
  clientStatusesRef.current = clientStatuses;
  displaySettingsRef.current = displaySettings;
  viewStateRef.current = viewState;
  onViewStateChangeRef.current = onViewStateChange;

  useEffect(() => {
    mapRef.current?.getSource('endpoints')?.setData(siteCollection(sites, selectedSiteId, clientStatuses, radioLinks));
    mapRef.current?.getSource('radio-link')?.setData(linkCollection(sites, radioLinks, selectedRadioLinkId, linkQuality));
    const map = mapRef.current;
    const maplibre = window.maplibregl;
    if (map && maplibre) syncLinkEndpointMarkers(map, maplibre, linkEndpointMarkersRef.current, sites, radioLinks, selectedRadioLinkId, linkQuality);
    const selected = sites.find((site) => site.id === selectedSiteId);
    if (!map || !selected) onSelectedSiteAnchorChange(null);
    else onSelectedSiteAnchorChange(map.project([selected.coordinates.lon, selected.coordinates.lat]));
  }, [sites, clientStatuses, radioLinks, selectedRadioLinkId, linkQuality, selectedSiteId, onSelectedSiteAnchorChange]);

  useEffect(() => {
    mapRef.current?.getSource('obstacles')?.setData(obstacleCollection(obstacles));
  }, [obstacles]);

  useEffect(() => {
    mapRef.current?.getSource('detected-obstacles')?.setData(detectedObstacleCollection(detectedObstacles));
  }, [detectedObstacles]);

  useEffect(() => {
    mapRef.current?.getSource('profile-hover')?.setData(profileHoverCollection(profileHoverCoordinates));
  }, [profileHoverCoordinates]);

  useEffect(() => {
    if (coverageOverlay) mapRef.current?.getSource('coverage-raster')?.updateImage?.({ url: coverageOverlay.imageUrl, coordinates: imageCoordinates(coverageOverlay) });
    mapRef.current?.getSource('interference-raster')?.updateImage?.({ url: interferenceOverlay?.imageUrl ?? TRANSPARENT_PIXEL, coordinates: interferenceOverlay ? imageCoordinates(interferenceOverlay) : [[39.8, 57.7], [40, 57.7], [40, 57.55], [39.8, 57.55]] });
    mapRef.current?.getSource('coverage-boundary')?.setData(coverageBoundaryCollection(coverageBoundary, displaySettings.showWeakCoverage));
  }, [coverageOverlay, interferenceOverlay, coverageBoundary, displaySettings.showWeakCoverage]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setPaintProperty('coverage-raster-layer', 'raster-opacity', displaySettings.coverageOpacity / 100);
    map.setPaintProperty('coverage-boundary-fill', 'fill-opacity', displaySettings.coverageOpacity / 100 * 0.3);
    map.setLayoutProperty('coverage-raster-layer', 'visibility', displaySettings.showCoverage ? 'visible' : 'none');
    map.setLayoutProperty('coverage-boundary-fill', 'visibility', displaySettings.showCoverage ? 'visible' : 'none');
    map.setLayoutProperty('coverage-boundary-line', 'visibility', displaySettings.showCoverage ? 'visible' : 'none');
    const stationFilter: unknown[] = displaySettings.showAllStations ? ['all'] : ['!', ['all', ['==', ['get', 'type'], 'client'], ['!=', ['get', 'selected'], 1]]];
    map.setFilter('endpoint-rings', stationFilter);
    map.setFilter('endpoint-labels', stationFilter);
  }, [displaySettings.coverageOpacity, displaySettings.showCoverage, displaySettings.showAllStations]);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setMaxZoom(mapMaximumZoom(displaySettings.mapStyle));
      mapRef.current.setStyle(buildMapStyle(displaySettings.mapStyle), { diff: false });
    }
  }, [displaySettings.mapStyle]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getCanvas().style.cursor = mapToolCursor(activeTool);
    const initialSite = activeTool === 'ptp' && radioDraftStartSiteId ? sites.find((site) => site.id === radioDraftStartSiteId) : null;
    ptpDraftStartRef.current = initialSite ? { siteId: initialSite.id, coordinates: initialSite.coordinates } : null;
    map.getSource('ptp-draft')?.setData(ptpDraftCollection(ptpDraftStartRef.current, null));
    if (activeTool !== 'measure') {
      measureDraftRef.current = emptyTwoPointMapDraft();
      map.getSource('measure-draft')?.setData(measureDraftCollection(measureDraftRef.current));
    }
    onRadioDraftStageChange(Boolean(ptpDraftStartRef.current));
  }, [activeTool, radioDraftStartSiteId, sites, onRadioDraftStageChange]);

  useEffect(() => {
    if (focusCoordinates) mapRef.current?.flyTo({ center: [focusCoordinates.lon, focusCoordinates.lat], zoom: 15, duration: 900 });
  }, [focusCoordinates]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !command) return;
    if (command.type === 'zoom-in') map.flyTo({ zoom: Math.min(24, map.getZoom() + 1), duration: 220 });
    if (command.type === 'zoom-out') map.flyTo({ zoom: Math.max(0, map.getZoom() - 1), duration: 220 });
    if (command.type === 'locate') navigator.geolocation?.getCurrentPosition((position) => map.flyTo({ center: [position.coords.longitude, position.coords.latitude], zoom: 15, duration: 650 }));
  }, [command]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !viewResetKey) return;
    const next = viewStateRef.current;
    map.jumpTo({ center: [next.center.lon, next.center.lat], zoom: next.zoom, pitch: next.pitch, bearing: next.bearing });
  }, [viewResetKey]);

  useEffect(() => {
    let retryTimer: number | undefined;
    let attempts = 0;
    let cancelled = false;
    let keyboardHandler: ((event: KeyboardEvent) => void) | null = null;
    let finishSiteDragHandler: (() => void) | null = null;

    const startMap = (): void => {
      if (cancelled || !containerRef.current || mapRef.current) return;
      const maplibre = window.maplibregl;
      if (!maplibre) {
        attempts += 1;
        if (attempts === 15) setWaitingForLibrary(true);
        if (attempts > 100) { setLoadFailed(true); return; }
        retryTimer = window.setTimeout(startMap, 100);
        return;
      }

      try {
        setWaitingForLibrary(false);
        const initialView = viewStateRef.current;
        const map = new maplibre.Map({ container: containerRef.current, style: buildMapStyle(displaySettingsRef.current.mapStyle), center: [initialView.center.lon, initialView.center.lat], zoom: initialView.zoom, maxZoom: mapMaximumZoom(displaySettingsRef.current.mapStyle), pitch: initialView.pitch, bearing: initialView.bearing, attributionControl: false });
        mapRef.current = map;
        const finishSiteDrag = (): void => {
          if (!draggingSiteIdRef.current) return;
          draggingSiteIdRef.current = null;
          callbacksRef.current.onMoveSiteEnd();
          map.dragPan.enable();
          map.getCanvas().style.cursor = mapToolCursor(activeToolRef.current);
        };
        finishSiteDragHandler = finishSiteDrag;
        map.addControl(new maplibre.NavigationControl({ showCompass: true }), 'bottom-right');
        map.addControl(new maplibre.AttributionControl({ compact: true }), 'bottom-right');
        const addDesignLayers = (): void => {
          if (map.getSource('radio-link')) return;
          const initialCoverage = coverageOverlayRef.current;
          map.addSource('coverage-raster', { type: 'image', url: initialCoverage?.imageUrl ?? TRANSPARENT_PIXEL, coordinates: initialCoverage ? imageCoordinates(initialCoverage) : [[39.8, 57.7], [40, 57.7], [40, 57.55], [39.8, 57.55]] });
          map.addLayer({ id: 'coverage-raster-layer', type: 'raster', source: 'coverage-raster', layout: { visibility: displaySettingsRef.current.showCoverage ? 'visible' : 'none' }, paint: { 'raster-opacity': displaySettingsRef.current.coverageOpacity / 100, 'raster-resampling': 'linear', 'raster-fade-duration': 0 } });
          const initialInterference = interferenceOverlayRef.current;
          map.addSource('interference-raster', { type: 'image', url: initialInterference?.imageUrl ?? TRANSPARENT_PIXEL, coordinates: initialInterference ? imageCoordinates(initialInterference) : [[39.8, 57.7], [40, 57.7], [40, 57.55], [39.8, 57.55]] });
          map.addLayer({ id: 'interference-raster-layer', type: 'raster', source: 'interference-raster', paint: { 'raster-opacity': 0.68, 'raster-resampling': 'linear', 'raster-fade-duration': 0 } });
          map.addSource('coverage-boundary', { type: 'geojson', data: coverageBoundaryCollection(coverageBoundaryRef.current, displaySettingsRef.current.showWeakCoverage) });
          map.addLayer({ id: 'coverage-boundary-fill', type: 'fill', source: 'coverage-boundary', paint: { 'fill-color': ['match', ['get', 'strength'], 0, '#35bfe6', 1, '#66c56f', 2, '#f2cf42', '#ef6d45'], 'fill-opacity': 0.2, 'fill-outline-color': 'rgba(8,124,240,.32)' } });
          map.addLayer({ id: 'coverage-boundary-line', type: 'line', source: 'coverage-boundary', paint: { 'line-color': '#087cf0', 'line-width': 1, 'line-opacity': 0.42, 'line-dasharray': [4, 3] } });
          map.addSource('radio-link', { type: 'geojson', data: linkCollection(sitesRef.current, radioLinksRef.current, selectedRadioLinkIdRef.current, linkQualityRef.current) });
          const qualityColor = ['case', ['==', ['get', 'selected'], 1], ['match', ['get', 'quality'], 'blocked', '#e5484d', 'marginal', '#f59e0b', '#087cf0'], ['case', ['==', ['get', 'kind'], 'ptmp'], '#64a8f4', '#8ab8e8']];
          map.addLayer({ id: 'radio-link-glow', type: 'line', source: 'radio-link', paint: { 'line-color': qualityColor, 'line-width': ['case', ['==', ['get', 'selected'], 1], 9, 5], 'line-opacity': 0.18, 'line-blur': 5 } });
          map.addLayer({ id: 'radio-link-line', type: 'line', source: 'radio-link', paint: { 'line-color': qualityColor, 'line-width': ['case', ['==', ['get', 'selected'], 1], 3, 1.7] } });
          map.addSource('profile-hover', { type: 'geojson', data: profileHoverCollection(profileHoverCoordinatesRef.current) });
          map.addLayer({ id: 'profile-hover-point', type: 'circle', source: 'profile-hover', paint: { 'circle-radius': 4, 'circle-color': '#ffffff', 'circle-stroke-color': '#087cf0', 'circle-stroke-width': 1.5 } });
          map.addSource('ptp-draft', { type: 'geojson', data: ptpDraftCollection(ptpDraftStartRef.current, null) });
          map.addLayer({ id: 'ptp-draft-line', type: 'line', source: 'ptp-draft', filter: ['==', ['geometry-type'], 'LineString'], paint: { 'line-color': '#087cf0', 'line-width': 2.4, 'line-opacity': 0.9, 'line-dasharray': [2, 1.4] } });
          map.addLayer({ id: 'ptp-draft-points', type: 'circle', source: 'ptp-draft', filter: ['==', ['geometry-type'], 'Point'], paint: { 'circle-radius': 7, 'circle-color': '#ffffff', 'circle-stroke-color': '#087cf0', 'circle-stroke-width': 3 } });
          map.addSource('measure-draft', { type: 'geojson', data: measureDraftCollection(measureDraftRef.current) });
          map.addLayer({ id: 'measure-draft-line', type: 'line', source: 'measure-draft', filter: ['==', ['geometry-type'], 'LineString'], paint: { 'line-color': '#087cf0', 'line-width': 1.8, 'line-opacity': 0.9, 'line-dasharray': [3, 2] } });
          map.addLayer({ id: 'measure-draft-points', type: 'circle', source: 'measure-draft', filter: ['==', ['geometry-type'], 'Point'], paint: { 'circle-radius': 4.5, 'circle-color': '#ffffff', 'circle-stroke-color': '#087cf0', 'circle-stroke-width': 2 } });
          map.addLayer({ id: 'measure-draft-label', type: 'symbol', source: 'measure-draft', filter: ['==', ['geometry-type'], 'LineString'], layout: { 'symbol-placement': 'line-center', 'text-field': ['get', 'label'], 'text-font': ['Open Sans Semibold'], 'text-size': 10, 'text-allow-overlap': true }, paint: { 'text-color': '#315b82', 'text-halo-color': 'rgba(255,255,255,.96)', 'text-halo-width': 2 } });
          map.addSource('endpoints', { type: 'geojson', data: siteCollection(sitesRef.current, selectedSiteIdRef.current, clientStatusesRef.current, radioLinksRef.current) });
          const stationFilter: unknown[] = displaySettingsRef.current.showAllStations ? ['all'] : ['!', ['all', ['==', ['get', 'type'], 'client'], ['!=', ['get', 'selected'], 1], ['!=', ['get', 'linked'], 1]]];
          map.addLayer({ id: 'endpoint-rings', type: 'circle', source: 'endpoints', filter: stationFilter, paint: { 'circle-radius': ['case', ['==', ['get', 'selected'], 1], 11, 8], 'circle-color': '#ffffff', 'circle-opacity': ['case', ['==', ['get', 'linked'], 1], 0, 1], 'circle-stroke-color': ['case', ['==', ['get', 'selected'], 1], '#087cf0', '#5b9fea'], 'circle-stroke-opacity': ['case', ['==', ['get', 'linked'], 1], 0, 1], 'circle-stroke-width': 3 } });
          map.addLayer({ id: 'endpoint-labels', type: 'symbol', source: 'endpoints', filter: stationFilter, layout: { 'text-field': ['get', 'label'], 'text-font': ['Open Sans Semibold'], 'text-size': 10, 'text-anchor': 'top', 'text-offset': [0, 1.35], 'text-allow-overlap': false }, paint: { 'text-color': '#45566a', 'text-halo-color': 'rgba(255,255,255,.96)', 'text-halo-width': 1.5 } });
          map.addSource('obstacles', { type: 'geojson', data: obstacleCollection(obstaclesRef.current) });
          map.addLayer({ id: 'obstacle-points', type: 'circle', source: 'obstacles', paint: { 'circle-radius': 7, 'circle-color': '#f3b75f', 'circle-stroke-color': '#07111f', 'circle-stroke-width': 2 } });
          map.addSource('detected-obstacles', { type: 'geojson', data: detectedObstacleCollection(detectedObstaclesRef.current) });
          map.addLayer({ id: 'detected-obstacles-fill', type: 'fill', source: 'detected-obstacles', paint: { 'fill-color': ['match', ['get', 'kind'], 'building', '#f3b75f', '#5f9b66'], 'fill-opacity': 0.28, 'fill-outline-color': ['match', ['get', 'kind'], 'building', '#f3b75f', '#72c67d'] } });
          syncLinkEndpointMarkers(map, maplibre, linkEndpointMarkersRef.current, sitesRef.current, radioLinksRef.current, selectedRadioLinkIdRef.current, linkQualityRef.current);
          map.getCanvas().style.cursor = mapToolCursor(activeToolRef.current);
        };
        map.on('style.load', addDesignLayers);

        map.on('click', (event) => {
          const coordinates = { lat: event.lngLat.lat, lon: event.lngLat.lng };
          const tool = activeToolRef.current;
          const siteType = toolSiteTypes[tool];
          if (siteType) {
            callbacksRef.current.onCreateSite(coordinates, siteType);
            return;
          }
          if (tool === 'obstacle') {
            callbacksRef.current.onCreateObstacle(coordinates);
            return;
          }
          if (tool === 'measure') {
            const draft = measureDraftRef.current;
            if (!draft.start) {
              measureDraftRef.current = beginTwoPointMapDraft(coordinates);
              map.getSource('measure-draft')?.setData(measureDraftCollection(measureDraftRef.current));
              return;
            }
            const completed = completeTwoPointMapDraft(draft, coordinates);
            if (completed.points) map.getSource('measure-draft')?.setData(measureDraftCollection({ start: completed.points[0], preview: completed.points[1] }));
            measureDraftRef.current = completed.draft;
            return;
          }
          if (tool === 'ptp') {
            const feature = map.queryRenderedFeatures(event.point, { layers: ['endpoint-rings'] })[0];
            const siteId = typeof feature?.properties?.id === 'string' ? feature.properties.id : null;
            const site = siteId ? sitesRef.current.find((item) => item.id === siteId) : null;
            const endpoint: MapRadioEndpoint = { siteId: site?.id ?? null, coordinates: site?.coordinates ?? coordinates };
            const start = ptpDraftStartRef.current;
            if (!start) {
              ptpDraftStartRef.current = endpoint;
              map.getSource('ptp-draft')?.setData(ptpDraftCollection(endpoint, coordinates));
              callbacksRef.current.onRadioDraftStageChange(true);
              return;
            }
            if (start.siteId && start.siteId === endpoint.siteId) return;
            ptpDraftStartRef.current = null;
            map.getSource('ptp-draft')?.setData(ptpDraftCollection(null, null));
            callbacksRef.current.onRadioDraftStageChange(false);
            callbacksRef.current.onCreateRadio('ptp', [start, endpoint]);
            return;
          }
          if (tool === 'ptmp') {
            const lengthPx = 118;
            const projected = (x: number, y: number): Coordinates => { const point = map.unproject({ x, y }); return { lat: point.lat, lon: point.lng }; };
            callbacksRef.current.onCreateRadio('ptmp', [coordinates, projected(event.point.x + lengthPx, event.point.y - 46), projected(event.point.x + lengthPx, event.point.y + 46)].map((point) => ({ siteId: null, coordinates: point })));
            return;
          }
          const closest = sitesRef.current
            .map((site) => ({ site, distanceM: haversineDistanceM(site.coordinates, coordinates) }))
            .sort((a, b) => a.distanceM - b.distanceM)[0];
          if (tool !== 'select') return;
          const endpointFeature = map.queryRenderedFeatures(event.point, { layers: ['endpoint-rings'] })[0];
          const endpointId = endpointFeature?.properties?.id;
          if (typeof endpointId === 'string') { callbacksRef.current.onSelectSite(endpointId); return; }
          const lineFeature = map.queryRenderedFeatures(event.point, { layers: ['radio-link-line'] })[0];
          const linkId = lineFeature?.properties?.id;
          if (typeof linkId === 'string') { callbacksRef.current.onSelectRadioLink(linkId); return; }
          callbacksRef.current.onSelectSite(closest && closest.distanceM < 1_500 ? closest.site.id : null);
        });

        map.on('contextmenu', (event) => {
          event.preventDefault();
          callbacksRef.current.onContextMenu({ lat: event.lngLat.lat, lon: event.lngLat.lng, x: event.point.x, y: event.point.y });
        });

        map.on('moveend', () => {
          const center = map.getCenter();
          onViewStateChangeRef.current({ center: { lat: center.lat, lon: center.lng }, zoom: map.getZoom(), pitch: map.getPitch(), bearing: map.getBearing() });
          const selected = sitesRef.current.find((site) => site.id === selectedSiteIdRef.current);
          callbacksRef.current.onSelectedSiteAnchorChange(selected ? map.project([selected.coordinates.lon, selected.coordinates.lat]) : null);
        });

        map.on('mousedown', (event) => {
          if (activeToolRef.current !== 'select') return;
          const feature = map.queryRenderedFeatures(event.point, { layers: ['endpoint-rings'] })[0];
          const id = feature?.properties?.id;
          if (typeof id !== 'string') return;
          draggingSiteIdRef.current = id;
          callbacksRef.current.onMoveSiteStart();
          map.dragPan.disable();
          map.getCanvas().style.cursor = 'grabbing';
          callbacksRef.current.onSelectSite(id);
          event.preventDefault();
        });

        map.on('mousemove', (event) => {
          const id = draggingSiteIdRef.current;
          if (id) {
            callbacksRef.current.onMoveSite(id, { lat: event.lngLat.lat, lon: event.lngLat.lng });
            callbacksRef.current.onSelectedSiteAnchorChange(event.point);
            return;
          }
          if (activeToolRef.current === 'ptp' && ptpDraftStartRef.current) {
            map.getSource('ptp-draft')?.setData(ptpDraftCollection(ptpDraftStartRef.current, { lat: event.lngLat.lat, lon: event.lngLat.lng }));
            return;
          }
          if (activeToolRef.current === 'measure' && measureDraftRef.current.start) {
            measureDraftRef.current = previewTwoPointMapDraft(measureDraftRef.current, { lat: event.lngLat.lat, lon: event.lngLat.lng });
            map.getSource('measure-draft')?.setData(measureDraftCollection(measureDraftRef.current));
            return;
          }
          if (activeToolRef.current === 'select') {
            const interactive = map.queryRenderedFeatures(event.point, { layers: ['endpoint-rings', 'radio-link-line'] })[0];
            map.getCanvas().style.cursor = interactive ? 'pointer' : 'default';
          }
        });

        map.on('mouseup', finishSiteDrag);

        keyboardHandler = (event: KeyboardEvent): void => {
          if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement) return;
          if (event.key === 'Escape' && activeToolRef.current !== 'select') {
            ptpDraftStartRef.current = null;
            measureDraftRef.current = emptyTwoPointMapDraft();
            map.getSource('ptp-draft')?.setData(ptpDraftCollection(null, null));
            map.getSource('measure-draft')?.setData(measureDraftCollection(measureDraftRef.current));
            callbacksRef.current.onRadioDraftStageChange(false);
            callbacksRef.current.onCancelActiveTool();
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }
          const horizontal = window.innerWidth * 0.75;
          const vertical = window.innerHeight * 0.75;
          if (event.key === 'Home') { map.panBy([-horizontal, 0], { duration: 260 }); event.preventDefault(); }
          if (event.key === 'End') { map.panBy([horizontal, 0], { duration: 260 }); event.preventDefault(); }
          if (event.key === 'PageUp') { map.panBy([0, -vertical], { duration: 260 }); event.preventDefault(); }
          if (event.key === 'PageDown') { map.panBy([0, vertical], { duration: 260 }); event.preventDefault(); }
        };
        window.addEventListener('keydown', keyboardHandler);
        window.addEventListener('mouseup', finishSiteDrag);
        window.addEventListener('pointerup', finishSiteDrag);
        window.addEventListener('pointercancel', finishSiteDrag);
        window.addEventListener('blur', finishSiteDrag);
      } catch {
        setLoadFailed(true);
      }
    };

    startMap();
    return () => {
      cancelled = true;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      if (keyboardHandler) window.removeEventListener('keydown', keyboardHandler);
      if (finishSiteDragHandler) {
        window.removeEventListener('mouseup', finishSiteDragHandler);
        window.removeEventListener('pointerup', finishSiteDragHandler);
        window.removeEventListener('pointercancel', finishSiteDragHandler);
        window.removeEventListener('blur', finishSiteDragHandler);
      }
      for (const entry of linkEndpointMarkersRef.current.values()) entry.marker.remove();
      linkEndpointMarkersRef.current.clear();
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  return (
    <div className="map" ref={containerRef} aria-label="Интерактивная карта проекта">
      {waitingForLibrary ? <div className="map-fallback subtle">Подключение картографического движка…</div> : null}
      {loadFailed ? <div className="map-fallback">Карта временно недоступна. Проект и расчёты остаются доступны.</div> : null}
    </div>
  );
}
